import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { IconArrowLeft } from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { CustomerIndexItemDto } from '@techgitter/contracts';
import { getCustomerIndexCached } from '../services/customerIndexCacheService';
import { CustomerDetailView } from '../components/customers/CustomerDetailView';
import { useCurrentUser } from '../contexts/CurrentUserContext';

/**
 * Standalone customer page reached via the `/customer/:dataverseCustomerId`
 * deep-link (the target the Print page's "Customer label" QR encodes). Like
 * QuoteDetailPage, this isn't a declared <Route> — MainLayout dispatches on the
 * pathname regex — so the id is read from the path rather than useParams().
 *
 * The id is either the Dataverse customer GUID or the RepairShopr customer id;
 * we resolve it against the cached customer index and hand the matched record
 * to the shared CustomerDetailView (the same view the Customers list opens).
 */
export const CustomerDetailPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Use the context's role check (the documented replacement for the legacy
  // userInfo.roles prop) so admin-only sections of CustomerDetailView appear
  // for admins reaching the page via deep-link.
  const currentUser = useCurrentUser();
  const isAdmin = currentUser?.hasRole('Admin') ?? false;

  const id = location.pathname.match(/^\/customer\/(.+)$/)?.[1] ?? '';
  const [customer, setCustomer] = useState<CustomerIndexItemDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCustomerIndexCached()
      .then((res) => {
        if (cancelled) return;
        // Resolve by Dataverse GUID or RepairShopr id so /customer/:id links
        // work with either identifier (UUID-shaped GUIDs and numeric RS ids
        // never collide).
        setCustomer(
          res.items.find(
            (c) => c.dataverseCustomerId === id || c.repairShoprCustomerId === id,
          ) ?? null,
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          p: 6,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Getting customer…
        </Typography>
      </Box>
    );
  }

  if (!customer) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          onClick={() => navigate('/')}
          startIcon={<IconArrowLeft size={16} stroke={1.5} />}
          sx={{ color: '#888', textTransform: 'none', mb: 2 }}
        >
          Back
        </Button>
        <Alert severity="error">{error ?? 'Customer not found.'}</Alert>
      </Box>
    );
  }

  return (
    <CustomerDetailView
      customer={customer}
      onBack={() => navigate('/')}
      isAdmin={isAdmin}
    />
  );
};

export default CustomerDetailPage;
